import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { TokenBase } from '../../utils/0x/token-registry.types';

const MAX_RECENTLY_VIEWED_COUNT = 20;
const RECENTLY_VIEWED_PERSIST_VERSION = 1;

/**
 * Minimum token info needed for recently viewed storage and display. This is
 * exactly the shared {@link TokenBase} shape, so a recently viewed token can be
 * selected and fed into a trade like any other token source.
 */
export type RecentToken = TokenBase;

interface SearchStore {
  recentlyViewed: RecentToken[];
  addRecentlyViewedToken: (token: RecentToken) => void;
  clearRecentlyViewedTokens: () => void;
}

export const useSearchStore = createWithEqualityFn<SearchStore>()(
  persist(
    (set, get) => ({
      recentlyViewed: [],
      addRecentlyViewedToken: (newRecentlyViewedToken: RecentToken) => {
        // Strip to only the fields defined by RecentToken to avoid persisting
        // transient data (e.g. balance snapshots) that would become stale.
        const {
          address,
          chainId,
          decimals,
          name,
          symbol,
          logo,
          isNativeUsdc,
          issuer,
          launchpad,
          verified,
          trusted,
        } = newRecentlyViewedToken;
        const cleanToken: RecentToken = {
          address,
          chainId,
          decimals,
          name,
          symbol,
          logo,
          ...(isNativeUsdc !== undefined && { isNativeUsdc }),
          ...(issuer !== undefined && { issuer }),
          ...(launchpad !== undefined && { launchpad }),
          ...(verified !== undefined && { verified }),
          ...(trusted !== undefined && { trusted }),
        };

        // add new recently viewed token to the start of the list
        const recentlyViewed = [
          cleanToken,
          ...get().recentlyViewed.filter(
            (token) =>
              !(
                token.address === newRecentlyViewedToken.address &&
                token.chainId === newRecentlyViewedToken.chainId
              ),
          ), // dedupe
        ].slice(0, MAX_RECENTLY_VIEWED_COUNT); // keep first n recently viewed

        set({
          recentlyViewed,
        });
      },
      clearRecentlyViewedTokens: () => {
        set({
          recentlyViewed: [],
        });
      },
    }),
    {
      name: 'matcha-search-storage',
      version: RECENTLY_VIEWED_PERSIST_VERSION,
      // `decimals` became a required field on RecentToken. Legacy entries
      // persisted without it would hydrate as malformed tokens, so drop any
      // that predate the field rather than carry an invalid shape.
      migrate: (persistedState, version) => {
        const state = persistedState as Partial<SearchStore> | undefined;
        if (!state?.recentlyViewed) return state as SearchStore;
        if (version < RECENTLY_VIEWED_PERSIST_VERSION) {
          return {
            ...state,
            recentlyViewed: state.recentlyViewed.filter(
              (token) => typeof token.decimals === 'number',
            ),
          } as SearchStore;
        }
        return state as SearchStore;
      },
    },
  ),
  shallow,
);

export const recentlyViewedTokensSelectors = (state: SearchStore) => ({
  recentlyViewed: state.recentlyViewed,
  addRecentlyViewedToken: state.addRecentlyViewedToken,
  clearRecentlyViewedTokens: state.clearRecentlyViewedTokens,
});
